import React, { useState, useEffect } from 'react';
import { Shield, Plus, X, Loader, AlertCircle } from 'lucide-react';
import { getUserSpamFilers, addUserSpamFiler, removeUserSpamFiler } from '../lib/spamFilerService';
import { supabase } from '../lib/supabase';

interface SpamFilerSettingsProps {
  userId: string;
}

export const SpamFilerSettings: React.FC<SpamFilerSettingsProps> = ({ userId }) => {
  const [spamFilers, setSpamFilers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [newFilerName, setNewFilerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSpamFilers();
  }, [userId]);

  const loadSpamFilers = async () => {
    try {
      setLoading(true);
      setError(null);
      const filers = await getUserSpamFilers(userId);
      setSpamFilers(filers);
    } catch (err) {
      console.error('Error loading spam filers:', err);
      setError('Failed to load spam filers');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newFilerName.trim()) {
      setError('Please enter a filer name');
      return;
    }

    try {
      setAdding(true);
      setError(null);
      setSuccess(null);

      const success = await addUserSpamFiler(userId, newFilerName.trim());
      if (success) {
        setNewFilerName('');
        setSuccess(`Added "${newFilerName.trim()}" to spam list`);
        await loadSpamFilers();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to add spam filer');
      }
    } catch (err) {
      console.error('Error adding spam filer:', err);
      setError('Failed to add spam filer');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (filerName: string) => {
    try {
      setRemoving(prev => new Set(prev).add(filerName));
      setError(null);
      setSuccess(null);

      const success = await removeUserSpamFiler(userId, filerName);
      if (success) {
        setSuccess(`Removed "${filerName}" from spam list`);
        await loadSpamFilers();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to remove spam filer');
      }
    } catch (err) {
      console.error('Error removing spam filer:', err);
      setError('Failed to remove spam filer');
    } finally {
      setRemoving(prev => {
        const next = new Set(prev);
        next.delete(filerName);
        return next;
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-black mb-3">Spam Filer Management</h3>
        <p className="text-sm text-gray-600 mb-4">
          Manage your personal list of spam filers. Filings from these filers will be filtered out from your view.
        </p>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center space-x-2">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Add New Spam Filer */}
        <div className="mb-6">
          <label htmlFor="fld2-SpamFilerSettingstsx-0" className="block text-sm font-medium text-gray-700 mb-2">
            Add Spam Filer
          </label>
          <div className="flex space-x-2">
            <input id="fld2-SpamFilerSettingstsx-0"
              type="text"
              value={newFilerName}
              onChange={(e) => setNewFilerName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAdd();
                }
              }}
              placeholder="Enter filer name (e.g., John Doe)"
              className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              disabled={adding}
            />
            <button
              onClick={handleAdd}
              disabled={adding || !newFilerName.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {adding ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Add</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Spam Filer List */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Spam Filer List ({spamFilers.length})
          </label>
          {loading ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center space-x-2">
              <Loader className="w-4 h-4 animate-spin text-gray-500" />
              <span className="text-gray-600 text-sm">Loading spam filers...</span>
            </div>
          ) : spamFilers.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">No spam filers added yet</p>
              <p className="text-gray-500 text-xs mt-1">
                Add filer names to filter out unwanted filings
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {spamFilers.map((filerName) => (
                <div
                  key={filerName}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    <span className="text-gray-900">{filerName}</span>
                  </div>
                  <button
                    onClick={() => handleRemove(filerName)}
                    disabled={removing.has(filerName)}
                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Remove ${filerName} from spam list`}
                  >
                    {removing.has(filerName) ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Your spam filer list works alongside the internal spam list. 
            Filings from filers in either list will be filtered from your view.
          </p>
        </div>
      </div>
    </div>
  );
};

